// CreateWelcomeTemplate.jsx
import React, { useEffect, useState } from "react";
import {
  Card,
  Input,
  Select,
  Upload,
  Button,
  InputNumber,
  Divider,
  message,
  Switch,
} from "antd";
import { PlusOutlined, UploadOutlined } from "@ant-design/icons";
import Snowfall from "react-snowfall";
import FireworksCanvas from "./FireworksCanvas";
import { useInfoUser } from "../../../store/UserStore";
import { useUploadHandler } from "../../../hooks/useUploadHandler";
import apiFactory from "../../../api";
import { getFileExtension } from "../../../utils/Utils";
import { conversationType, MESSAGE_STATUS } from "../../../config/Constant";
import { v4 as uuidv4 } from "uuid";
import { useImageLoader } from "../../../components/customHook/ImageLoader";
import { uploadFileV2 } from "../../../utils/SendFile";
const { TextArea } = Input;

const iconOptions = {
  common: ["⭐", "✅", "📢", "💡", "🔥", "🎯", "⚡", "🔔", "📌"],
  love: ["💖", "💘", "💝", "💌", "😍", "🥰", "💞", "💓"],
  tech: ["💻", "🖥", "📱", "⚙️", "🔧", "🔌", "🧠", "🤖", "🚀"],
  celebration: ["🎉", "🎁", "🎂", "🥳", "🎆", "🍾", "🎊"],
  nature: ["🌸", "🌿", "🌞", "🌙", "🍁", "🌈", "❄️"],
  christmas: ["🎄", "⛄", "🎅", "🔔", "🎶", "🦌"],
};

const defaultSlide = {
  slideId: uuidv4(),
  image: "",
  imagePreview: "",
  backgroundImage: "",
  backgroundImagePreview: "",
  titleEn: "Welcome",
  titleVn: "",
  titleKr: "",
  descriptionEn: "Welcome to our system",
  descriptionVn: "",
  descriptionKr: "",
  leftIcon: "",
  rightIcon: "",
  backgroundColor: "",
};

export default function CreateWelcomeTemplate({
  activeTab,
  editingSlide,
  onFinishEdit,
}) {
  const [themeSetting, setThemeSetting] = useState({
    theme: "",
    background: "#4199cc",
    effect: "",
    titleFont: "serif",
    descFont: "sans-serif",
    titleSize: 36,
    descSize: 16,
    titleColor: "#ffffff",
    descColor: "#ffffff",
  });

  const [slides, setSlides] = useState([{ ...defaultSlide }]);
  const [saving, setSaving] = useState(false);
  const [openLeft, setOpenLeft] = useState(null);
  const [openRight, setOpenRight] = useState(null);

  const updateTheme = (key, value) =>
    setThemeSetting((s) => ({ ...s, [key]: value }));

  const updateSlide = (index, key, value) => {
    setSlides((prev) => {
      const clone = [...prev];
      clone[index] = { ...clone[index], [key]: value };
      return clone;
    });
  };

  const addSlide = () => setSlides((s) => [...s, createDefaultSlide()]);

  const createDefaultSlide = () => ({
    ...defaultSlide,
    slideId: uuidv4(),
  });

  const removeSlide = (index) =>
    setSlides((prev) => prev.filter((_, i) => i !== index));

  const getFontFamily = (font) => {
    if (font === "serif") return "serif";
    if (font === "monospace") return "monospace";
    return font;
  };

  const handleUploadImage = async (file, idx) => {
    const reader = new FileReader();

    reader.onload = async () => {
      try {
        const controller = new AbortController();
        const res = await uploadFileV2(file, null, "FREE", controller.signal);

        if (res?.resourceId) {
          updateSlide(idx, "image", res?.resourceId);
        }
      } catch (err) {
        console.error(err);
      }
    };

    reader.readAsDataURL(file);
  };

  const handleUploadBackground = async (file, idx) => {
    const reader = new FileReader();

    reader.onload = async () => {
      const controller = new AbortController();
      try {
        const res = await uploadFileV2(file, null, "FREE", controller.signal);

        if (res?.resourceId) {
          updateSlide(idx, "backgroundImage", res?.resourceId);
        }
      } catch (e) {
        console.error(e);
      }
    };

    reader.readAsDataURL(file);
  };

  const buildPayload = () =>
    slides.map((s, i) => ({
      slideId: s.slideId,
      centerImage: s.image,
      titleEn: s.titleEn,
      titleVn: s.titleVn,
      titleKr: s.titleKr,
      descriptionEn: s.descriptionEn,
      descriptionVn: s.descriptionVn,
      descriptionKr: s.descriptionKr,
      leftIcon: s.leftIcon,
      rightIcon: s.rightIcon,
      backgroundColor: s.backgroundColor,
      backgroundImage: s.backgroundImage,
      themeName: themeSetting.theme,
      background: themeSetting.background,
      effect: themeSetting.effect,
      titleFont: themeSetting.titleFont,
      descFont: themeSetting.descFont,
      titleSize: themeSetting.titleSize,
      descSize: themeSetting.descSize,
      titleColor: themeSetting.titleColor,
      descColor: themeSetting.descColor,
    }));

  const handleSave = async () => {
    const payload = buildPayload();
    setSaving(true);
    try {
      const res = await apiFactory.welcomeSlideApi.saveSlides(payload);
      if (!res.status === 200) message.error("Save failed");

      message.success(
        editingSlide ? "Cập nhật slide thành công" : "Tạo slide thành công",
      );
      editingSlide && onFinishEdit?.(res?.data[0] || {});
    } catch (err) {
      console.error(err);
      message.error("Lưu thất bại");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!editingSlide) return;

    setSlides([
      {
        slideId: editingSlide.slideId,
        image: editingSlide.centerImage || "",
        backgroundImage: editingSlide.backgroundImage || "",
        titleEn: editingSlide.titleEn,
        titleVn: editingSlide.titleVn,
        titleKr: editingSlide.titleKr,
        descriptionEn: editingSlide.descriptionEn,
        descriptionVn: editingSlide.descriptionVn,
        descriptionKr: editingSlide.descriptionKr,
        leftIcon: editingSlide.leftIcon,
        rightIcon: editingSlide.rightIcon,
        backgroundColor: editingSlide.backgroundColor,
      },
    ]);

    setThemeSetting({
      theme: editingSlide.themeName || "",
      background: editingSlide.background || "#4199cc",
      effect: editingSlide.effect || "none",
      titleFont: editingSlide.titleFont || "serif",
      descFont: editingSlide.descFont || "sans-serif",
      titleSize: editingSlide.titleSize || 36,
      descSize: editingSlide.descSize || 16,
      titleColor: editingSlide.titleColor || "#ffffff",
      descColor: editingSlide.descColor || "#ffffff",
    });
  }, [editingSlide]);

  useEffect(() => {
    if (activeTab === "2" && !editingSlide) {
      setSlides([{ ...defaultSlide, slideId: uuidv4() }]);
      setThemeSetting({
        theme: "",
        background: "#4199cc",
        effect: "",
        titleFont: "serif",
        descFont: "sans-serif",
        titleSize: 36,
        descSize: 16,
        titleColor: "#ffffff",
        descColor: "#ffffff",
      });
    }
  }, [activeTab]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (e.target.closest("[data-dropdown]")) return;

      setOpenLeft(null);
      setOpenRight(null);
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  return (
    <div className="space-y-6">
      <Card title="Theme Setting">
        <div className="grid grid-cols-5 gap-3 p-4">
          {/* ROW 1 */}
          <div>
            <p className="mb-1 text-xs font-semibold">Theme</p>
            <Input
              value={themeSetting.theme}
              onChange={(e) => updateTheme("theme", e.target.value)}
              placeholder="Theme name"
            />
          </div>

          <div>
            <p className="mb-1 text-xs font-semibold">Title Font</p>
            <Select
              value={themeSetting.titleFont}
              onChange={(v) => updateTheme("titleFont", v)}
              className="w-full"
            >
              <Select.Option value="serif">Serif</Select.Option>
              <Select.Option value="sans-serif">Sans</Select.Option>
              <Select.Option value="monospace">Mono</Select.Option>
              <Select.Option value="Galada">Galada</Select.Option>
            </Select>
          </div>

          <div>
            <p className="mb-1 text-xs font-semibold">Desc Font</p>
            <Select
              value={themeSetting.descFont}
              onChange={(v) => updateTheme("descFont", v)}
              className="w-full"
            >
              <Select.Option value="serif">Serif</Select.Option>
              <Select.Option value="sans-serif">Sans</Select.Option>
              <Select.Option value="monospace">Mono</Select.Option>
              <Select.Option value="Galada">Galada</Select.Option>
            </Select>
          </div>

          <div>
            <p className="mb-1 text-xs font-semibold">Title Size</p>
            <InputNumber
              className="w-full"
              value={themeSetting.titleSize}
              onChange={(v) => updateTheme("titleSize", v)}
            />
          </div>

          <div>
            <p className="mb-1 text-xs font-semibold">Desc Size</p>
            <InputNumber
              className="w-full"
              value={themeSetting.descSize}
              onChange={(v) => updateTheme("descSize", v)}
            />
          </div>

          {/* ROW 2 */}
          <div>
            <p className="mb-1 text-xs font-semibold">Background Color</p>
            <Input
              type="color"
              value={themeSetting.background}
              onChange={(e) => updateTheme("background", e.target.value)}
            />
          </div>

          <div>
            <p className="mb-1 text-xs font-semibold">Title Color</p>
            <Input
              type="color"
              value={themeSetting.titleColor}
              onChange={(e) => updateTheme("titleColor", e.target.value)}
            />
          </div>

          <div>
            <p className="mb-1 text-xs font-semibold">Desc Color</p>
            <Input
              type="color"
              value={themeSetting.descColor}
              onChange={(e) => updateTheme("descColor", e.target.value)}
            />
          </div>

          <div />
          <div />

          {/* EFFECT */}
          <div className="col-span-5">
            <div className="flex items-center gap-4 px-3 py-2 border rounded">
              <span className="text-xs font-semibold">Effect</span>

              <div className="flex items-center gap-2">
                <span className="text-xs">Snow</span>
                <Switch
                  checked={themeSetting.effect === "snow"}
                  onChange={(v) => updateTheme("effect", v ? "snow" : "none")}
                />
              </div>

              <div className="flex items-center gap-2">
                <span className="text-xs">Firework</span>
                <Switch
                  checked={themeSetting.effect === "firework"}
                  onChange={(v) =>
                    updateTheme("effect", v ? "firework" : "none")
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </Card>

      <div className="space-y-6">
        {slides.map((slide, idx) => {
          return (
            <div key={idx} className="grid grid-cols-2 gap-6">
              <Card
                title={`Slide ${idx + 1} Setting`}
                extra={
                  <Button danger onClick={() => removeSlide(idx)}>
                    Remove
                  </Button>
                }
              >
                <div className="space-y-4 p-4">
                  <div>
                    <p className="font-semibold mb-1">Background Color</p>
                    <Input
                      type="color"
                      value={slide.backgroundColor || themeSetting.background}
                      onChange={(e) =>
                        updateSlide(idx, "backgroundColor", e.target.value)
                      }
                    />
                  </div>
                  {/* BACKGROUND IMAGE */}
                  <ImageUploadField
                    label="Background Image"
                    buttonText="Upload Background"
                    resourceId={slide.backgroundImage}
                    onUpload={(file) => handleUploadBackground(file, idx)}
                    onRemove={() => updateSlide(idx, "backgroundImage", null)}
                  />

                  <ImageUploadField
                    label="Center Image"
                    buttonText="Upload Image"
                    resourceId={slide.image}
                    onUpload={(file) => handleUploadImage(file, idx)}
                    onRemove={() => updateSlide(idx, "image", null)}
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <div className="w-full relative" data-dropdown>
                      <p className="mb-1 font-semibold">Left Icon</p>
                      <div className="relative">
                        <div
                          className="border border-gray-300 rounded-md px-3 py-2 bg-white cursor-pointer 
                          hover:border-blue-400 transition flex items-center justify-between"
                          onClick={() =>
                            setOpenLeft(openLeft === idx ? null : idx)
                          }
                        >
                          <span className="truncate">
                            {slide.leftIcon || "Select icon..."}
                          </span>

                          {slide.leftIcon && (
                            <span
                              onClick={(e) => {
                                e.stopPropagation();
                                updateSlide(idx, "leftIcon", null);
                              }}
                              className="ml-2 text-gray-400 hover:text-red-500 cursor-pointer text-sm"
                            >
                              ✕
                            </span>
                          )}
                        </div>
                      </div>

                      {openLeft === idx && (
                        <div
                          className="absolute left-0 right-0 mt-1 bg-white shadow-lg rounded-md border border-gray-300
                        max-h-64 overflow-y-auto p-3 z-50"
                        >
                          {Object.entries(iconOptions).map(([group, icons]) => (
                            <div key={group} className="mb-4">
                              <p className="text-xs text-gray-500 mb-2 font-semibold">
                                {group.toUpperCase()}
                              </p>

                              <div className="flex flex-wrap gap-2">
                                {icons.map((icon) => (
                                  <div
                                    key={icon}
                                    onClick={() => {
                                      updateSlide(idx, "leftIcon", icon);
                                      setOpenLeft(null);
                                    }}
                                    className={`cursor-pointer text-xl border rounded-md px-2 py-1 
                                    transition select-none
                                    ${
                                      slide.leftIcon === icon
                                        ? "bg-blue-500 text-white border-blue-500"
                                        : "border-gray-300 hover:bg-gray-100"
                                    }
                                  `}
                                  >
                                    {icon}
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="w-full relative" data-dropdown>
                      <p className="mb-1 font-semibold">Right Icon</p>

                      <div className="relative">
                        <div
                          className="border border-gray-300 rounded-md px-3 py-2 bg-white cursor-pointer 
               hover:border-blue-400 transition flex items-center justify-between"
                          onClick={() =>
                            setOpenRight(openRight === idx ? null : idx)
                          }
                        >
                          <span className="truncate">
                            {slide.rightIcon || "Select icon..."}
                          </span>

                          {slide.rightIcon && (
                            <span
                              onClick={(e) => {
                                e.stopPropagation();
                                updateSlide(idx, "rightIcon", null);
                              }}
                              className="ml-2 text-gray-400 hover:text-red-500 cursor-pointer text-sm"
                            >
                              ✕
                            </span>
                          )}
                        </div>
                      </div>

                      {openRight === idx && (
                        <div
                          className="absolute left-0 right-0 mt-1 bg-white shadow-lg rounded-md border border-gray-300
                        max-h-64 overflow-y-auto p-3 z-50"
                        >
                          {Object.entries(iconOptions).map(([group, icons]) => (
                            <div key={group} className="mb-4">
                              <p className="text-xs text-gray-500 mb-2 font-semibold">
                                {group.toUpperCase()}
                              </p>

                              <div className="flex flex-wrap gap-2">
                                {icons.map((icon) => (
                                  <div
                                    key={icon}
                                    onClick={() => {
                                      updateSlide(idx, "rightIcon", icon);
                                      setOpenRight(null);
                                    }}
                                    className={`cursor-pointer text-xl border rounded-md px-2 py-1 
                                    transition select-none
                                    ${
                                      slide.rightIcon === icon
                                        ? "bg-green-500 text-white border-green-500"
                                        : "border-gray-300 hover:bg-gray-100"
                                    }
                                  `}
                                  >
                                    {icon}
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <p className="font-semibold">Title</p>

                    <Input
                      placeholder="Title (EN)"
                      value={slide.titleEn || ""}
                      onChange={(e) =>
                        updateSlide(idx, "titleEn", e.target.value)
                      }
                    />

                    <Input
                      placeholder="Tiêu đề (VN)"
                      value={slide.titleVn || ""}
                      onChange={(e) =>
                        updateSlide(idx, "titleVn", e.target.value)
                      }
                    />

                    <Input
                      placeholder="제목 (KR)"
                      value={slide.titleKr || ""}
                      onChange={(e) =>
                        updateSlide(idx, "titleKr", e.target.value)
                      }
                    />
                  </div>

                  <div className="flex flex-col gap-1">
                    <p className="font-semibold mb-1">Description</p>
                    <TextArea
                      rows={2}
                      placeholder="Description (EN)"
                      value={slide.descriptionEn}
                      onChange={(e) =>
                        updateSlide(idx, "descriptionEn", e.target.value)
                      }
                    />

                    <TextArea
                      rows={2}
                      placeholder="Mô tả (VN)"
                      value={slide.descriptionVn}
                      onChange={(e) =>
                        updateSlide(idx, "descriptionVn", e.target.value)
                      }
                    />

                    <TextArea
                      rows={2}
                      placeholder="설명 (KR)"
                      value={slide.descriptionKr}
                      onChange={(e) =>
                        updateSlide(idx, "descriptionKr", e.target.value)
                      }
                    />
                  </div>
                </div>
              </Card>

              {/* ==============================preview=================================== */}

              <Card title={`Slide ${idx + 1} Preview`} className="relative">
                <SlidePreview
                  slide={slide}
                  themeSetting={themeSetting}
                  getFontFamily={getFontFamily}
                />
              </Card>
            </div>
          );
        })}

        <Button type="dashed" block icon={<PlusOutlined />} onClick={addSlide}>
          Add Slide
        </Button>
      </div>

      <div className="flex justify-end gap-4">
        <Button type="primary" loading={saving} onClick={handleSave}>
          Save Template
        </Button>
      </div>
    </div>
  );
}

const ImageUploadField = React.memo(function ImageUploadField({
  label,
  buttonText,
  resourceId,
  onUpload,
  onRemove,
}) {
  const { imageUrl } = useImageLoader(resourceId);

  return (
    <div>
      <p className="font-semibold mb-1">{label}</p>

      <div className="flex items-stretch gap-3">
        {/* UPLOAD */}
        <Upload
          showUploadList={false}
          beforeUpload={(file) => {
            onUpload(file);
            return false;
          }}
        >
          <Button
            type="primary"
            icon={<UploadOutlined />}
            className="
              h-[42px] px-4 rounded-lg
              border border-gray-300
              hover:bg-gray-50 transition
              whitespace-nowrap
              w-[200px]
            "
          >
            {buttonText}
          </Button>
        </Upload>

        {/* PREVIEW */}
        {imageUrl && (
          <div className="flex items-center gap-3 flex-1 h-[42px] px-3 rounded-lg border border-gray-300 bg-gray-50">
            <img
              src={imageUrl}
              className="w-9 h-9 rounded object-cover border"
              alt=""
            />

            <span className="flex-1 text-sm truncate">{label}</span>

            <button
              onClick={onRemove}
              className="text-red-500 text-sm hover:underline"
            >
              Remove
            </button>
          </div>
        )}
      </div>
    </div>
  );
});

// ================= SLIDE PREVIEW (INLINE COMPONENT) =================
const SlidePreview = React.memo(function SlidePreview({
  slide,
  themeSetting,
  getFontFamily,
}) {
  const { user } = useInfoUser();

  const { imageUrl: centerImageUrl } = useImageLoader(slide.image || null);
  const { imageUrl: backgroundImageUrl } = useImageLoader(
    slide.backgroundImage || null,
  );

  return (
    <div className="w-full h-[500px] flex items-center justify-center">
      <div
        className="relative w-full max-w-3xl mx-auto aspect-video overflow-hidden"
        style={{
          backgroundColor: slide.backgroundColor || themeSetting.background,

          backgroundImage: backgroundImageUrl
            ? `url(${backgroundImageUrl})`
            : slide.backgroundImagePreview
              ? `url(${slide.backgroundImagePreview})`
              : "none",

          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {/* EFFECT */}
        {themeSetting.effect === "snow" && (
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <Snowfall snowflakeCount={150} />
          </div>
        )}

        {themeSetting.effect === "firework" && (
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <FireworksCanvas />
          </div>
        )}

        {/* CONTENT */}
        <div className="absolute inset-0 flex flex-col items-center justify-center p-4 text-center">
          {(centerImageUrl || slide.imagePreview) && (
            <img
              src={centerImageUrl || slide.imagePreview}
              alt=""
              className="w-64 max-h-[60%] mb-4 object-contain"
            />
          )}

          <h1
            className="font-bold flex items-center gap-3"
            style={{
              fontFamily: getFontFamily(themeSetting.titleFont),
              fontSize: themeSetting.titleSize,
              color: themeSetting.titleColor,
            }}
          >
            <span>{slide.leftIcon}</span>
            <span>
              {user?.language === "vn"
                ? slide?.titleVn || slide?.titleEn || slide?.titleKr
                : user?.language === "kr"
                  ? slide?.titleKr || slide?.titleEn || slide?.titleVn
                  : slide?.titleEn}
            </span>
            <span>{slide.rightIcon}</span>
          </h1>

          <p
            className="mt-3 max-w-md"
            style={{
              fontFamily: getFontFamily(themeSetting.descFont),
              fontSize: themeSetting.descSize,
              color: themeSetting.descColor,
            }}
          >
            {user?.language === "vn"
              ? slide?.descriptionVn ||
                slide?.descriptionEn ||
                slide?.descriptionKr
              : user?.language === "kr"
                ? slide?.descriptionKr ||
                  slide?.descriptionEn ||
                  slide?.descriptionVn
                : slide?.descriptionEn}
          </p>
        </div>
      </div>
    </div>
  );
});
